import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Users,
  Plus,
  RefreshCw,
  DollarSign,
  Copy,
  Check,
  Pencil
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminAffiliatesPage = () => {
  const { getAuthHeader } = useAuth();
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAffiliate, setEditingAffiliate] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    commission_rate: 20,
    payment_email: "",
    payment_method: "paypal",
    notes: ""
  });

  const fetchAffiliates = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/affiliates`, {
        headers: getAuthHeader()
      });
      setAffiliates(response.data);
    } catch (error) {
      toast.error("Failed to load affiliates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAffiliates();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) {
      toast.error("Please fill required fields");
      return;
    }
    
    setSubmitting(true);
    try {
      if (editingAffiliate) {
        await axios.put(`${API}/admin/affiliates/${editingAffiliate.id}`, formData, {
          headers: getAuthHeader()
        });
        toast.success("Affiliate updated");
      } else {
        await axios.post(`${API}/admin/affiliates`, formData, {
          headers: getAuthHeader()
        });
        toast.success("Affiliate created");
      }
      setDialogOpen(false);
      resetForm();
      fetchAffiliates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      company: "",
      commission_rate: 20,
      payment_email: "",
      payment_method: "paypal",
      notes: ""
    });
    setEditingAffiliate(null);
  };

  const handleEdit = (affiliate) => {
    setEditingAffiliate(affiliate);
    setFormData({
      name: affiliate.name,
      email: affiliate.email,
      company: affiliate.company || "",
      commission_rate: affiliate.commission_rate,
      payment_email: affiliate.payment_email || "",
      payment_method: affiliate.payment_method || "paypal",
      notes: affiliate.notes || ""
    });
    setDialogOpen(true);
  };

  const copyAffiliateId = async (id) => {
    try {
      await navigator.clipboard.writeText(id);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
      toast.success("Affiliate ID copied!");
    } catch (error) {
      toast.info(`Affiliate ID: ${id}`);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
      inactive: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20",
      suspended: "bg-red-500/10 text-red-500 border-red-500/20"
    };
    return variants[status] || variants.inactive;
  };

  return (
    <div className="space-y-6" data-testid="admin-affiliates-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-mono font-bold tracking-tight flex items-center gap-3">
            <Users className="w-7 h-7 text-primary" />
            Affiliates
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage affiliate partners and commissions
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchAffiliates} className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-2" data-testid="create-affiliate-btn">
                <Plus className="w-4 h-4" />
                Add Affiliate
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border max-w-md">
              <DialogHeader>
                <DialogTitle>{editingAffiliate ? "Edit Affiliate" : "Add New Affiliate"}</DialogTitle>
                <DialogDescription>
                  {editingAffiliate ? "Update affiliate information." : "Create a new affiliate partner."}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="bg-background"
                      data-testid="affiliate-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="bg-background"
                      data-testid="affiliate-email-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Company</Label>
                  <Input
                    value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                    className="bg-background"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Commission Rate (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={formData.commission_rate}
                      onChange={(e) => setFormData({...formData, commission_rate: parseFloat(e.target.value)})}
                      className="bg-background"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Payment Method</Label>
                    <Select value={formData.payment_method} onValueChange={(v) => setFormData({...formData, payment_method: v})}>
                      <SelectTrigger className="bg-background">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="paypal">PayPal</SelectItem>
                        <SelectItem value="stripe">Stripe</SelectItem>
                        <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                        <SelectItem value="wise">Wise</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Payment Email</Label>
                  <Input
                    type="email"
                    value={formData.payment_email}
                    onChange={(e) => setFormData({...formData, payment_email: e.target.value})}
                    placeholder="If different from contact email"
                    className="bg-background"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Internal notes about this affiliate..."
                    className="bg-background"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {editingAffiliate ? "Update" : "Create"} Affiliate
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="p-4">
          <p className="text-sm">
            <strong>How to use:</strong> Share the affiliate's ID with them. When creating licenses, 
            enter their ID in the "Affiliate Code" field to track referrals and calculate commissions.
          </p>
        </CardContent>
      </Card>

      {/* Affiliates Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="affiliates-grid">
        {loading ? (
          [...Array(6)].map((_, i) => (
            <Card key={i} className="bg-card/50 border-border">
              <CardContent className="p-4">
                <div className="skeleton h-24 w-full" />
              </CardContent>
            </Card>
          ))
        ) : affiliates.length > 0 ? (
          affiliates.map((affiliate) => (
            <Card key={affiliate.id} className="bg-card/50 border-border hover:border-primary/30 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-medium">{affiliate.name}</h3>
                    <p className="text-xs text-muted-foreground">{affiliate.email}</p>
                    {affiliate.company && (
                      <p className="text-xs text-muted-foreground">{affiliate.company}</p>
                    )}
                  </div>
                  <Badge className={getStatusBadge(affiliate.status)}>
                    {affiliate.status}
                  </Badge>
                </div>
                
                <div className="grid grid-cols-2 gap-4 my-4">
                  <div className="text-center p-2 bg-background rounded">
                    <p className="text-xs text-muted-foreground">Referrals</p>
                    <p className="text-lg font-mono font-bold">{affiliate.total_referrals}</p>
                  </div>
                  <div className="text-center p-2 bg-background rounded">
                    <p className="text-xs text-muted-foreground">Revenue</p>
                    <p className="text-lg font-mono font-bold">${affiliate.total_revenue.toLocaleString()}</p>
                  </div>
                </div>
                
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
                  <span>Commission: {affiliate.commission_rate}%</span>
                  <span className="capitalize">{affiliate.payment_method}</span>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 gap-1"
                    onClick={() => copyAffiliateId(affiliate.id)}
                  >
                    {copiedId === affiliate.id ? (
                      <Check className="w-3 h-3 text-emerald-500" />
                    ) : (
                      <Copy className="w-3 h-3" />
                    )}
                    Copy ID
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 gap-1"
                    onClick={() => handleEdit(affiliate)}
                  >
                    <Pencil className="w-3 h-3" />
                    Edit
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card className="md:col-span-2 lg:col-span-3 bg-card/50 border-border">
            <CardContent className="p-8 text-center">
              <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No affiliates yet</p>
              <p className="text-sm text-muted-foreground">Create your first affiliate partner to start tracking referrals.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminAffiliatesPage;
